import { useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import AuraControlledAutocomplete from 'app/main/apps/common/AuraControlledAutocomplete';
import { getShippingCarriers } from 'app/main/apps/backoffice/store/shipmentOrderSlice';

/**
 * PGSS-5407: carrier + tracking (PRO) number for a shipment that has already left the warehouse.
 *
 * The dispatch dialog captures both, but the PRO number is often only issued when the carrier
 * physically picks the freight up — after dispatch has moved the shipment on. This is the surface
 * for filling it in, or correcting a mistyped one, without touching the shipment's status.
 */
function UpdateTrackingFormContent() {
  const dispatch = useDispatch();
  const { control, formState: { errors } } = useFormContext();
  const shippingCarrierNames = useSelector(
    ({ backofficeApp }) => backofficeApp?.shipmentOrder?.shippingCarrierNames
  );

  useEffect(() => {
    if (!shippingCarrierNames?.length) {
      dispatch(getShippingCarriers());
    }
  }, []);

  return (
    <div className="flex w-full flex-col">
      <Typography className="mb-16 font-mono text-md tracking-wider" color="text.secondary">
        The tracking number is shown on the Tracking # column of the shipment list and on the
        shipment view, and links to the carrier&apos;s tracking page where one is configured.
      </Typography>
      <div className="flex flex-col md:flex-row w-full gap-10">
        <div className="flex w-full">
          <AuraControlledAutocomplete
            labelName="Carrier"
            control={control}
            controlName="carrier"
            inputValues={shippingCarrierNames || []}
            defaultValue=""
            className="mt-0 mb-20"
            error={!!errors.carrier}
            helperText={errors?.carrier?.message}
            fullWidth
            size="small"
          />
        </div>
        <div className="flex w-full">
          <Controller
            control={control}
            name="tracking_number"
            render={({ field }) => (
              <TextField
                {...field}
                className="mb-20"
                label="Tracking / PRO Number"
                id="tracking_number"
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.tracking_number}
                helperText={errors?.tracking_number?.message}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default UpdateTrackingFormContent;
